"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";

/**
 * Sticky bottom CTA for /hardware — shows after 50% scroll,
 * hides while #hardware-quote is in view (matches hardware.html DS-E).
 */
export default function HardwareStickyCta() {
  const [pastThreshold, setPastThreshold] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const denom = document.documentElement.scrollHeight - window.innerHeight;
      const depth = denom > 0 ? (window.scrollY / denom) * 100 : 0;
      setPastThreshold(depth > 50);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const form = document.getElementById("hardware-quote");
    if (!form || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      entries => {
        setFormVisible(entries[0]?.isIntersecting ?? false);
      },
      { threshold: 0.2 }
    );
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  const open = pastThreshold && !formVisible && !dismissed;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-navy py-3 px-4 shadow-[0_-12px_40px_-20px_rgba(0,0,0,0.5)]",
        "transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform",
        "motion-reduce:transition-none",
        open
          ? "translate-y-0 opacity-100 pointer-events-auto"
          : "translate-y-full opacity-0 pointer-events-none"
      )}
      aria-hidden={!open}
    >
      <div className="container mx-auto flex flex-col items-center justify-between gap-3 px-4 sm:flex-row xl:px-0">
        <p className="text-center text-sm text-white sm:text-left">
          See what a turnkey rollout looks like for your sites.
        </p>
        <div className="flex items-center gap-3">
          <Link
            href="#hardware-quote"
            className="whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
            data-lead="hardware-sticky"
            tabIndex={open ? 0 : -1}
          >
            Scope Your Rollout
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="px-2 text-sm text-white/60 transition-colors hover:text-white"
            aria-label="Close"
            tabIndex={open ? 0 : -1}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
